/**
 * 구매대장 (§5.29, v1.20) — 단일 원천.
 *
 * 영수증·견적서·거래명세서·주문 캡처 같은 증빙을 올리면 AI가 항목을 읽어 초안을 만들고,
 * 교직원이 확인해 저장하면 구매대장의 한 행이 된다. 대장 PDF는 여기 정의한 **기본 양식**으로
 * 그리되, 원마다 쓰는 서식이 달라 시설별 덮어쓰기(DB `PurchaseLedgerTemplate.config`)를
 * 허용한다 — 제목·열 구성·열 이름·결재란·하단 문구를 원장이 앱에서 바꾼다(배포 없음).
 * 새 열이 필요하면 `PURCHASE_LEDGER_COLUMNS`에 더하고 API의 PDF 렌더러에서 값을 채운다.
 */
/** 구매 분류 — 코드만 DB에 넣고 라벨은 화면·PDF에서 붙인다 */
export const PURCHASE_CATEGORIES = [
    { code: 'TEACHING', label: '교재·교구' },
    { code: 'FOOD', label: '급·간식 재료' },
    { code: 'SUPPLIES', label: '소모품' },
    { code: 'EQUIPMENT', label: '비품' },
    { code: 'FACILITY', label: '시설·관리' },
    { code: 'EVENT', label: '행사' },
    { code: 'HYGIENE', label: '위생·안전' },
    { code: 'OTHER', label: '기타' },
];
export const PURCHASE_CATEGORY_CODES = PURCHASE_CATEGORIES.map((c) => c.code);
export const PAYMENT_METHODS = [
    { code: 'CARD', label: '카드' },
    { code: 'CASH', label: '현금' },
    { code: 'TRANSFER', label: '계좌이체' },
    { code: 'OTHER', label: '기타' },
];
export const PAYMENT_METHOD_CODES = PAYMENT_METHODS.map((m) => m.code);
/** 증빙 종류 — 올린 자료가 무엇인지. AI가 추정하고 교직원이 고친다 */
export const PURCHASE_EVIDENCE_KINDS = [
    { code: 'RECEIPT', label: '영수증' },
    { code: 'QUOTE', label: '견적서' },
    { code: 'STATEMENT', label: '거래명세서' },
    { code: 'CARD_SLIP', label: '카드전표' },
    { code: 'CAPTURE', label: '주문 캡처' },
    { code: 'OTHER', label: '기타' },
];
export const PURCHASE_EVIDENCE_KIND_CODES = PURCHASE_EVIDENCE_KINDS.map((k) => k.code);
export function purchaseCategoryLabel(code) {
    return PURCHASE_CATEGORIES.find((c) => c.code === code)?.label ?? '기타';
}
export function paymentMethodLabel(code) {
    return PAYMENT_METHODS.find((m) => m.code === code)?.label ?? '';
}
export function purchaseEvidenceKindLabel(code) {
    return PURCHASE_EVIDENCE_KINDS.find((k) => k.code === code)?.label ?? '';
}
/**
 * 대장에 놓을 수 있는 열 전체 목록(카탈로그). 순서는 기본 순서다.
 * 시설 양식은 이 중 일부를 골라 순서를 바꾸고 이름을 바꿀 수 있다.
 */
export const PURCHASE_LEDGER_COLUMNS = [
    { key: 'no', label: '번호', width: 5, align: 'center' },
    { key: 'date', label: '구매일', width: 10, align: 'center' },
    { key: 'category', label: '분류', width: 10, align: 'center' },
    { key: 'item', label: '품목', width: 20, align: 'left' },
    { key: 'spec', label: '규격', width: 10, align: 'left' },
    { key: 'quantity', label: '수량', width: 6, align: 'right' },
    { key: 'unitPrice', label: '단가', width: 10, align: 'right' },
    { key: 'amount', label: '금액', width: 11, align: 'right' },
    { key: 'vendor', label: '거래처', width: 13, align: 'left' },
    { key: 'payment', label: '결제', width: 8, align: 'center' },
    { key: 'purpose', label: '용도·비고', width: 18, align: 'left' },
    { key: 'evidence', label: '증빙', width: 9, align: 'center' },
    { key: 'recorder', label: '담당', width: 8, align: 'center' },
];
export const PURCHASE_LEDGER_COLUMN_KEYS = PURCHASE_LEDGER_COLUMNS.map((c) => c.key);
export const PURCHASE_LEDGER_LIMITS = {
    titleMax: 40,
    labelMax: 12,
    signatureMax: 8,
    signaturesMax: 5,
    footnoteMax: 200,
};
/** 기본 양식 — 현장에서 흔히 쓰는 구매(지출)대장 열 구성을 재구성했다 */
export const DEFAULT_PURCHASE_LEDGER_TEMPLATE = {
    title: '구매대장',
    columns: [
        'no',
        'date',
        'category',
        'item',
        'quantity',
        'unitPrice',
        'amount',
        'vendor',
        'payment',
        'purpose',
        'evidence',
    ],
    labels: {},
    signatures: ['담당', '원장'],
    footnote: '',
    showTotals: true,
};
function isColumnKey(value) {
    return typeof value === 'string' && PURCHASE_LEDGER_COLUMN_KEYS.includes(value);
}
/**
 * 시설 덮어쓰기(JSON)를 기본 양식 위에 얹어 완전한 양식으로 만든다.
 * 알 수 없는 열·빈 값·너무 긴 문자열은 조용히 버린다 — 저장된 JSON이 깨져 있어도
 * 대장 출력이 멈추지 않게.
 */
export function resolvePurchaseLedgerTemplate(override) {
    const base = DEFAULT_PURCHASE_LEDGER_TEMPLATE;
    const fresh = () => ({
        ...base,
        columns: [...base.columns],
        labels: {},
        signatures: [...base.signatures],
    });
    if (!override || typeof override !== 'object' || Array.isArray(override))
        return fresh();
    const raw = override;
    const resolved = fresh();
    const limits = PURCHASE_LEDGER_LIMITS;
    if (typeof raw.title === 'string' && raw.title.trim()) {
        resolved.title = raw.title.trim().slice(0, limits.titleMax);
    }
    if (Array.isArray(raw.columns)) {
        const columns = [...new Set(raw.columns.filter(isColumnKey))];
        if (columns.length > 0)
            resolved.columns = columns;
    }
    if (raw.labels && typeof raw.labels === 'object' && !Array.isArray(raw.labels)) {
        for (const [key, value] of Object.entries(raw.labels)) {
            if (isColumnKey(key) && typeof value === 'string' && value.trim()) {
                resolved.labels[key] = value.trim().slice(0, limits.labelMax);
            }
        }
    }
    if (Array.isArray(raw.signatures)) {
        resolved.signatures = raw.signatures
            .filter((s) => typeof s === 'string' && s.trim().length > 0)
            .map((s) => s.trim().slice(0, limits.signatureMax))
            .slice(0, limits.signaturesMax);
    }
    if (typeof raw.footnote === 'string') {
        resolved.footnote = raw.footnote.trim().slice(0, limits.footnoteMax);
    }
    if (typeof raw.showTotals === 'boolean')
        resolved.showTotals = raw.showTotals;
    return resolved;
}
/** 양식에 적용된 열 이름 — 덮어쓰기가 있으면 그것, 없으면 카탈로그 기본 이름 */
export function purchaseColumnLabel(template, key) {
    return (template.labels[key] ?? PURCHASE_LEDGER_COLUMNS.find((c) => c.key === key)?.label ?? key);
}
// ── AI 증빙 읽기 ────────────────────────────────────────────
/** 교직원 1인이 하루에 증빙을 읽힐 수 있는 횟수 (§5.23 한도와 별도 계정) */
export const PURCHASE_EXTRACT_DAILY_LIMIT = 40;
/**
 * 증빙 파일 크기 상한 — 모델에 원본을 그대로 보내므로 사진은 클라이언트가
 * 긴 변 1600px로 줄여 올린다(lib/upload.ts). 이 값을 넘는 파일은 읽지 않는다.
 */
export const PURCHASE_EVIDENCE_MAX_BYTES = 4_500_000;
/** 증빙 사진의 긴 변 상한(px) — 모델이 권장하는 크기 근처로 줄여 데이터와 비용을 아낀다 */
export const PURCHASE_EVIDENCE_IMAGE_EDGE = 1600;
/** 화면 문구 — 교직원 전용 화면이지만 한곳에 모아 둔다 */
export const PURCHASE_LEDGER_COPY = {
    menu: '구매대장',
    intro: '영수증·견적서·주문 캡처를 올리면 항목을 읽어 대장에 정리해 드려요. 저장 전에 한 번만 확인해 주세요.',
    emptyTitle: '이달 구매 기록이 없어요',
    emptyBody: '증빙 사진이나 PDF를 올리면 구매대장에 바로 정리돼요.',
    aiDraftNotice: 'AI가 증빙에서 읽은 초안이에요. 금액과 날짜를 꼭 확인한 뒤 저장해 주세요.',
    aiUnavailable: 'AI 도우미가 아직 설정되지 않아 증빙만 첨부됐어요. 항목은 직접 적어 주세요.',
    aiUnavailableHint: 'AI 도우미가 설정되면 증빙을 읽어 항목을 채워 드려요. 지금은 증빙을 첨부한 뒤 직접 적어요.',
    templateHint: '원에서 쓰는 서식에 맞게 제목·열·결재란을 바꿀 수 있어요. 대장 PDF에 바로 반영돼요.',
};
//# sourceMappingURL=purchase-ledger.js.map